import type { paths } from '@luxuryescapes/contract-svc-lux-loyalty'
import { isValidDate } from 'lib/datetime/dateUtils'

type EnrollmentProgressResponse = paths['/api/lux-loyalty/claims/campaigns/{campaignId}/enrollment-progress']['get']['responses']['200']['content']['application/json']
type EnrollResponse = paths['/api/lux-loyalty/claims/campaigns/{campaignId}/enroll']['post']['responses']['200']['content']['application/json']

export type StatusMatchEnrollmentProgress = EnrollmentProgressResponse['result']
export type StatusMatchEnrollmentInfo = EnrollResponse['result']['enrollment']

export type StatusMatchEnrollResult = EnrollResponse['result'] & {
  /** Set by the FE modal — the program the user selected */
  selectedProgram?: string
  /** Set by the FE modal — the source tier the user selected */
  selectedTier?: string
}

/** Société tiers a status match or challenge can grant. Drives both the payload types and validation. */
export const STATUS_MATCH_TARGET_TIERS = ['Silver', 'Gold'] as const
export type StatusMatchTargetTier = typeof STATUS_MATCH_TARGET_TIERS[number]

export interface StatusMatchProgram {
  name: string
  /** Cloudinary image id. When omitted, the program name is shown instead. */
  logoImageId?: string
  tiers: Array<{
    sourceTier: string
    targetSocieteTier: StatusMatchTargetTier
  }>
}

/**
 * Evidence config variants — determines the application modal form layout.
 * Discriminated by `type`. Add new variants here as new modal types are introduced.
 */
export type StatusMatchEvidence = ProgramTierPhotosEvidence

/**
 * Variant: 2 cascading dropdowns (program → tier) + photo upload.
 * The `fieldNames` object maps our predefined field roles to the BE's
 * evidenceFields[].name values (which become keys in the submitted `evidence` JSON).
 */
export interface ProgramTierPhotosEvidence {
  type: 'programTierPhotos'
  fieldNames: {
    program: string
    tier: string
    photo: string
  }
  verificationImageId?: string
  minPhotos: number
  photoHints: Array<string>
  acceptedFormats: Array<string>
  proofRequirements: Array<string>
}

export const STATUS_MATCH_EVIDENCE_TYPES: ReadonlyArray<StatusMatchEvidence['type']> = ['programTierPhotos']

export interface StatusMatchHero {
  title: string
  subtitle: string
  description: string
  /** Match campaigns only — challenge campaigns have no apply CTA */
  ctaText?: string
  imageId: string
  /** Challenge campaigns only — shown to logged-in members enrolled in the campaign */
  eligibleLabel?: string
  /** Challenge campaigns only — shown to logged-in members not enrolled in the campaign */
  ineligibleLabel?: string
  /** Challenge campaigns only — shown to enrolled customers who still need to join Société. Falls back to eligibleLabel. */
  eligibleNonMemberLabel?: string
}

export interface StatusMatchApplicationPeriod {
  startsAt: string
  endsAt: string
}

export interface StatusMatchFaq {
  question: string
  answer: string
}

export interface StatusMatchCondition {
  icon: 'clock' | 'calendar' | 'star' | 'lock'
  title: string
  description: string
}

/** Pill shown above each how-it-works step. Omit to fall back to the legacy position-based icon. */
export type StatusMatchHowItWorksIcon = 'tiers' | 'upload' | 'book' | 'status' | 'none'

export interface StatusMatchHowItWorksStep {
  title: string
  description: string
  icon?: StatusMatchHowItWorksIcon
}

/** Optional overrides for a section's hardcoded heading copy. An empty string hides that line. */
export interface StatusMatchSectionCopy {
  heading?: string
  subtitle?: string
}

export interface StatusMatchTierBenefitsCopy extends StatusMatchSectionCopy {
  /** Text above each tier card's benefit list */
  unlockText?: string
}

export interface StatusMatchSuccessStep {
  /** Matches the BE's matchCheckConfig.checks[].key — used to pair with eligible status */
  key: string
  title: string
  description: string
}

export type ChallengeCardBackground =
  | { type: 'tierGradient' }
  | { type: 'image'; imageId: string }
  | { type: 'color'; value: string }

export interface ChallengeCardConfig {
  background: ChallengeCardBackground
  /** Template with {targetTier}, {expiresDate} placeholders */
  cardTitle: string
  /** Template with {targetTier}, {expiresDate} placeholders */
  cardSubtitle: string
  /** Template with {targetTier}, {expiresDate} placeholders */
  cardDescription?: string
}

/**
 * Copy for the "You're enrolled" modal. Omitted fields fall back to the modal's default copy.
 * Supports {firstName}, {targetTier} and {expiresDate} placeholders.
 */
export interface StatusMatchEnrolledModalCopy {
  headline?: string
  description?: string
  startsNowTitle?: string
  startsNowSubtitle?: string
  stepsTitle?: string
  benefitsTitle?: string
  ctaText?: string
  /** Site-relative path for the primary CTA (must start with "/"; anything else is ignored) */
  ctaUrl?: string
}

export interface StatusMatchEnrolledModalConfig extends StatusMatchEnrolledModalCopy {
  /** Kill switch without removing the copy. Defaults to true. */
  enabled?: boolean
  /**
   * Extra region-stripped route patterns where the modal won't show, same syntax as Lab's modal pop-up
   * route blacklist (e.g. "/purchase/*"). Added to DEFAULT_ENROLLED_MODAL_ROUTE_BLACKLIST, never replacing it.
   */
  routeBlacklist?: Array<string>
}

export type StatusMatchCampaignType = 'match' | 'challenge'

interface StatusMatchLabPayloadBase {
  slug: string
  campaignId: string
  allowedRegions: Array<string>
  /** When true, all Apply/CTA buttons are disabled and application sections are hidden */
  restrictApplications?: boolean
  /** Campaign application window */
  applicationPeriod: StatusMatchApplicationPeriod
  /** Warning banner shown at the top of the page (e.g. when campaign has expired) */
  expiredBanner?: {
    message: string
  }
  /**
   * Banner shown to a member whose own enrollment expired unfulfilled. Takes priority over `expiredBanner`.
   * Supports {tierName}, {expiredOn} and {nextEligibleFrom} placeholders.
   * Omitted: match campaigns use the default copy; challenge campaigns show nothing (the default is match-only).
   */
  enrollmentExpiredBanner?: {
    message: string
  }
  hero: StatusMatchHero
  conditions?: Array<StatusMatchCondition>
  howItWorks?: Array<StatusMatchHowItWorksStep>
  howItWorksCopy?: StatusMatchSectionCopy
  tierBenefitsCopy?: StatusMatchTierBenefitsCopy
  conditionsCopy?: StatusMatchSectionCopy
  successSteps?: Array<StatusMatchSuccessStep>
  challengeCard?: ChallengeCardConfig
  /** "You're enrolled" modal shown once when a member returns to the site. Omit to disable. */
  enrolledModal?: StatusMatchEnrolledModalConfig
  faqs: Array<StatusMatchFaq>
  terms: Array<string>
  termsSlug: string
}

/** Members apply with proof of partner status, reviewed by CS */
export interface StatusMatchMatchPayload extends StatusMatchLabPayloadBase {
  /** Omitted on legacy payloads — treated as 'match' */
  type?: 'match'
  programs: Array<StatusMatchProgram>
  evidence: StatusMatchEvidence
}

/** Members are enrolled in the background — no application, evidence or review */
export interface StatusMatchChallengePayload extends StatusMatchLabPayloadBase {
  type: 'challenge'
  targetTier: StatusMatchTargetTier
}

export type StatusMatchLabPayload = StatusMatchMatchPayload | StatusMatchChallengePayload

export function isChallengeCampaign(payload: StatusMatchLabPayload): payload is StatusMatchChallengePayload {
  return payload.type === 'challenge'
}

export interface StatusMatchCampaignConfig {
  config: StatusMatchLabPayload
}

export function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0
}

function isKnownEvidenceType(value: unknown): value is StatusMatchEvidence['type'] {
  return typeof value === 'string' && (STATUS_MATCH_EVIDENCE_TYPES as ReadonlyArray<string>).includes(value)
}

function isValidEvidence(value: unknown): value is StatusMatchEvidence {
  if (!isObject(value)) return false
  if (!isKnownEvidenceType(value.type)) return false

  if (value.type === 'programTierPhotos') {
    return (
      isObject(value.fieldNames) &&
      isNonEmptyString(value.fieldNames.program) &&
      isNonEmptyString(value.fieldNames.tier) &&
      isNonEmptyString(value.fieldNames.photo) &&
      typeof value.minPhotos === 'number' &&
      Array.isArray(value.photoHints) &&
      Array.isArray(value.acceptedFormats) &&
      Array.isArray(value.proofRequirements)
    )
  }

  return false
}

function isValidTargetTier(value: unknown): value is StatusMatchTargetTier {
  return typeof value === 'string' && (STATUS_MATCH_TARGET_TIERS as ReadonlyArray<string>).includes(value)
}

function isValidTypeSpecificFields(payload: Record<string, unknown>): boolean {
  if (payload.type === 'challenge') {
    return isValidTargetTier(payload.targetTier)
  }
  if (payload.type !== undefined && payload.type !== 'match') return false
  return (
    Array.isArray(payload.programs) &&
    payload.programs.length > 0 &&
    isValidEvidence(payload.evidence)
  )
}

export function isStatusMatchLabPayload(
  payload: unknown,
): payload is StatusMatchLabPayload {
  if (!isObject(payload)) return false
  return (
    isNonEmptyString(payload.slug) &&
    isNonEmptyString(payload.campaignId) &&
    Array.isArray(payload.allowedRegions) &&
    isObject(payload.applicationPeriod) &&
    isNonEmptyString(payload.applicationPeriod.startsAt) &&
    isNonEmptyString(payload.applicationPeriod.endsAt) &&
    isValidDate(new Date(payload.applicationPeriod.startsAt)) &&
    isValidDate(new Date(payload.applicationPeriod.endsAt)) &&
    isObject(payload.hero) &&
    isNonEmptyString(payload.hero.title) &&
    isNonEmptyString(payload.hero.imageId) &&
    isValidTypeSpecificFields(payload) &&
    Array.isArray(payload.faqs) &&
    Array.isArray(payload.terms) &&
    isNonEmptyString(payload.termsSlug)
  )
}
